import { useState } from "react";
import type { StudioJobArtifacts, SectionArtifact } from "../../client";
import { getSectionVideoUrl, getFinalVideoUrl, getAllSectionsCode } from "../../client";
import { AnalyticsDashboard } from "./AnalyticsDashboard";
import { ExperimentPanel } from "./ExperimentPanel";

type ArtifactInspectorProps = {
  artifacts: StudioJobArtifacts;
  onSelectSection?: (section: SectionArtifact) => void;
  onEditSection?: (section: SectionArtifact) => void;
  selectedSectionId?: string | null;
};

type TabType = "overview" | "outline" | "storyboard" | "sections" | "experiments" | "analytics";

export function ArtifactInspector({
  artifacts,
  onSelectSection,
  onEditSection,
  selectedSectionId,
}: ArtifactInspectorProps) {
  const [activeTab, setActiveTab] = useState<TabType>("overview");

  const tabs: { id: TabType; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "outline", label: "Outline" },
    { id: "storyboard", label: "Storyboard" },
    { id: "sections", label: "Sections" },
    { id: "experiments", label: "Experiments" },
    { id: "analytics", label: "Analytics" },
  ];

  return (
    <div style={{ color: "#e0e0e0" }}>
      <style>{`
        .artifact-tabs {
          display: flex;
          flex-wrap: wrap;
          border-bottom: 1px solid #333;
          margin-bottom: 1rem;
          gap: 0;
        }
        .artifact-tabs button {
          flex-shrink: 0;
        }
      `}</style>
      {/* Tabs */}
      <div className="artifact-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "0.5rem 1rem",
              background: "none",
              border: "none",
              borderBottom: activeTab === tab.id ? "2px solid #4a90d9" : "2px solid transparent",
              color: activeTab === tab.id ? "#4a90d9" : "#888",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: activeTab === tab.id ? 600 : 400,
              transition: "all 0.15s ease",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && <OverviewTab artifacts={artifacts} />}
      {activeTab === "outline" && <OutlineTab artifacts={artifacts} />}
      {activeTab === "storyboard" && <StoryboardTab artifacts={artifacts} />}
      {activeTab === "sections" && (
        <SectionsTab
          artifacts={artifacts}
          onSelectSection={onSelectSection}
          onEditSection={onEditSection}
          selectedSectionId={selectedSectionId}
        />
      )}
      {activeTab === "experiments" && <ExperimentPanel selectedJobDir={artifacts.directory} />}
      {activeTab === "analytics" && <AnalyticsDashboard />}
    </div>
  );
}

function OverviewTab({ artifacts }: { artifacts: StudioJobArtifacts }) {
  const outline = artifacts.outline as Record<string, unknown> | null;
  const topic = (outline?.topic as string) || (outline?.title as string) || artifacts.topic_slug;

  return (
    <div>
      <style>{`
        .artifact-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .artifact-tabs {
          display: flex;
          flex-wrap: wrap;
          border-bottom: 1px solid #333;
          margin-bottom: 1rem;
          gap: 0;
        }
        @media (max-width: 480px) {
          .artifact-stats-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      {/* Title */}
      <h2 style={{ margin: "0 0 1rem 0", fontSize: "1.5rem", fontWeight: 600, wordBreak: "break-word" }}>{topic}</h2>

      {/* Stats Grid */}
      <div className="artifact-stats-grid">
        <StatCard label="Sections" value={artifacts.sections.length} />
        <StatCard
          label="Rendered"
          value={artifacts.sections.filter((s) => s.status === "rendered").length}
        />
        <StatCard
          label="Voiceovers"
          value={artifacts.voiceover_files.length}
        />
      </div>

      {/* Final Video Preview */}
      {artifacts.final_video && (
        <div style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ margin: "0 0 0.5rem 0", fontSize: "1rem", fontWeight: 500 }}>
            Final Video
          </h3>
          <video
            src={getFinalVideoUrl(artifacts.directory)}
            controls
            style={{
              width: "100%",
              maxHeight: "400px",
              borderRadius: "8px",
              backgroundColor: "#000",
            }}
          />
        </div>
      )}

      {/* Directory Info */}
      <div style={{ marginTop: "1rem" }}>
        <h3 style={{ margin: "0 0 0.5rem 0", fontSize: "0.875rem", fontWeight: 500, color: "#888" }}>
          Directory
        </h3>
        <code
          style={{
            display: "block",
            padding: "0.5rem",
            backgroundColor: "#1a1a1a",
            borderRadius: "4px",
            fontSize: "0.75rem",
            wordBreak: "break-all",
          }}
        >
          {artifacts.directory}
        </code>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div
      style={{
        padding: "1rem",
        backgroundColor: "#1a1a1a",
        borderRadius: "8px",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: "1.5rem", fontWeight: 600 }}>{value}</div>
      <div style={{ fontSize: "0.75rem", color: "#888" }}>{label}</div>
    </div>
  );
}

function OutlineTab({ artifacts }: { artifacts: StudioJobArtifacts }) {
  if (!artifacts.outline) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
        No outline available
      </div>
    );
  }

  return (
    <div>
      <JsonViewer data={artifacts.outline} />
    </div>
  );
}

function StoryboardTab({ artifacts }: { artifacts: StudioJobArtifacts }) {
  const [showAssets, setShowAssets] = useState(false);
  const storyboard = showAssets
    ? artifacts.storyboard_with_assets || artifacts.storyboard
    : artifacts.storyboard;

  if (!storyboard) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
        No storyboard available
      </div>
    );
  }

  return (
    <div>
      {artifacts.storyboard_with_assets && (
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={showAssets}
              onChange={(e) => setShowAssets(e.target.checked)}
            />
            <span style={{ fontSize: "0.875rem" }}>Show with assets</span>
          </label>
        </div>
      )}
      <JsonViewer data={storyboard} />
    </div>
  );
}

function SectionsTab({
  artifacts,
  onSelectSection,
  onEditSection,
  selectedSectionId,
}: {
  artifacts: StudioJobArtifacts;
  onSelectSection?: (section: SectionArtifact) => void;
  onEditSection?: (section: SectionArtifact) => void;
  selectedSectionId?: string | null;
}) {
  const [copyStatus, setCopyStatus] = useState<"idle" | "loading" | "copied" | "error">("idle");

  const handleCopyAllCode = async () => {
    if (artifacts.sections.length === 0) return;

    setCopyStatus("loading");
    try {
      const sectionIds = artifacts.sections.map((s) => s.section_id);
      const titles = artifacts.sections.map((s) => s.title);
      const allCode = await getAllSectionsCode(artifacts.directory, sectionIds, titles);
      await navigator.clipboard.writeText(allCode);
      setCopyStatus("copied");
      setTimeout(() => setCopyStatus("idle"), 2000);
    } catch {
      setCopyStatus("error");
      setTimeout(() => setCopyStatus("idle"), 2000);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {artifacts.sections.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
          No sections available
        </div>
      ) : (
        <>
          {/* Copy All Code Button */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.5rem" }}>
            <button
              onClick={handleCopyAllCode}
              disabled={copyStatus === "loading"}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: copyStatus === "copied" ? "#1b5e20" : copyStatus === "error" ? "#b71c1c" : "#2a2a2a",
                border: "1px solid #444",
                borderRadius: "6px",
                color: "#e0e0e0",
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: copyStatus === "loading" ? "wait" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.15s ease",
              }}
            >
              {copyStatus === "loading" ? (
                <>
                  <span style={{ animation: "spin 1s linear infinite" }}>...</span>
                  Copying...
                </>
              ) : copyStatus === "copied" ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Copied!
                </>
              ) : copyStatus === "error" ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  Error
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Copy All Code
                </>
              )}
            </button>
          </div>
          {artifacts.sections.map((section) => (
          <div
            key={section.section_id}
            onClick={() => onSelectSection?.(section)}
            style={{
              padding: "1rem",
              backgroundColor: selectedSectionId === section.section_id ? "#2a2a2a" : "#1a1a1a",
              border:
                selectedSectionId === section.section_id
                  ? "1px solid #4a90d9"
                  : "1px solid #333",
              borderRadius: "8px",
              cursor: onSelectSection ? "pointer" : "default",
              transition: "all 0.15s ease",
            }}
          >
            {/* Section Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 500 }}>{section.title}</div>
                <div style={{ fontSize: "0.75rem", color: "#888" }}>
                  Section {section.section_id}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <StatusBadge status={section.status} />
                {onEditSection && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditSection(section);
                    }}
                    style={{
                      padding: "0.25rem 0.5rem",
                      backgroundColor: "#1976d2",
                      border: "none",
                      borderRadius: "4px",
                      color: "#fff",
                      fontSize: "0.7rem",
                      fontWeight: 500,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit
                  </button>
                )}
              </div>
            </div>

            {/* Video Preview (if rendered) */}
            {section.status === "rendered" && section.video_path && (
              <div style={{ marginTop: "0.75rem" }}>
                <video
                  src={getSectionVideoUrl(artifacts.directory, section.section_id)}
                  controls
                  style={{
                    width: "100%",
                    maxHeight: "200px",
                    borderRadius: "4px",
                    backgroundColor: "#000",
                  }}
                />
              </div>
            )}

            {/* Error indicator */}
            {section.has_error_log && (
              <div
                style={{
                  marginTop: "0.5rem",
                  fontSize: "0.75rem",
                  color: "#f44336",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
              >
                <span>Has error log</span>
              </div>
            )}
          </div>
        ))}
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; color: string }> = {
    rendered: { bg: "#1b5e20", color: "#a5d6a7" },
    code_only: { bg: "#e65100", color: "#ffcc80" },
    pending: { bg: "#424242", color: "#bdbdbd" },
    failed: { bg: "#b71c1c", color: "#ef9a9a" },
  };

  const style = styles[status] || styles.pending;

  return (
    <span
      style={{
        padding: "0.25rem 0.5rem",
        backgroundColor: style.bg,
        color: style.color,
        borderRadius: "4px",
        fontSize: "0.75rem",
        fontWeight: 500,
      }}
    >
      {status.replace("_", " ")}
    </span>
  );
}

function JsonViewer({ data }: { data: Record<string, unknown> }) {
  return (
    <pre
      style={{
        backgroundColor: "#0d0d0d",
        padding: "1rem",
        borderRadius: "8px",
        overflow: "auto",
        maxHeight: "60vh",
        fontSize: "0.75rem",
        lineHeight: 1.5,
        margin: 0,
      }}
    >
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}
